import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './hooks/useAuthStore'
import { useSocket } from './hooks/useSocket'
import LoginPage from './pages/LoginPage'
import LandingPage from './pages/LandingPage'
import LobbyPage from './pages/LobbyPage'
import GamePage from './pages/GamePage'
import SettingsPage from './pages/SettingsPage'
import AdminPage from './pages/admin/AdminPage'
import Layout from './components/Layout'
import SoundTest from './sound/SoundTest'
import { useEffect, useState, Suspense } from 'react'

function App() {
  const { user, isAdmin, isLoading, initializeAuth } = useAuthStore()
  const { connect, disconnect } = useSocket()

  // State to track if user has visited landing page
  const [hasVisitedLanding, setHasVisitedLanding] = useState(() => {
    return localStorage.getItem('hasVisitedLanding') === 'true'
  })

  useEffect(() => {
    return initializeAuth()
  }, [initializeAuth])

  useEffect(() => {
    if (user) {
      connect(user)
    } else {
      disconnect()
    }

    return () => disconnect()
    // Key on user.id, not the user object: disconnect() tears down the
    // socket AND nulls currentGame (ejecting GamePage to the lobby), so a
    // mere object-identity change (e.g. updateUser) must not trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, connect, disconnect])

  // Handle when user clicks "Get Started" on landing page
  const handleGetStarted = () => {
    setHasVisitedLanding(true)
    localStorage.setItem('hasVisitedLanding', 'true')
  }

  // Handle when user clicks "Back to Home" on login page
  const handleBackToHome = () => {
    setHasVisitedLanding(false)
    localStorage.removeItem('hasVisitedLanding')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    )
  }

  if (!user) {
    // Show landing page for first-time visitors, login page for returning visitors
    if (!hasVisitedLanding) {
      return (
        <Suspense fallback={<div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="text-white text-xl">Loading...</div></div>}>
          <LandingPage onGetStarted={handleGetStarted} />
        </Suspense>
      )
    }
    return (
      <Suspense fallback={<div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="text-white text-xl">Loading...</div></div>}>
        <LoginPage onBackToHome={handleBackToHome} />
      </Suspense>
    )
  }

  return (
    <Suspense fallback={<div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="text-white text-xl">Loading...</div></div>}>
      <Routes>
        {/* Standalone, admin-only — deliberately outside Layout and not linked from nav */}
        <Route path="/admin" element={isAdmin ? <AdminPage /> : <Navigate to="/lobby" replace />} />
        <Route
          path="/*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<Navigate to="/lobby" replace />} />
                <Route path="/lobby" element={<LobbyPage />} />
                <Route path="/game/:gameId" element={<GamePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/sound-test" element={<SoundTest />} />
                <Route path="*" element={<Navigate to="/lobby" replace />} />
              </Routes>
            </Layout>
          }
        />
      </Routes>
    </Suspense>
  )
}

export default App